import { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { useAppThunkDispatch } from 'state/store';
import { fetchProjects } from 'state/ProjectsList/projectsListSlice';
import {
  selectQuickFilters,
  setQuickFilters as setQuickFiltersAction,
  upsertFiltersSettings,
} from 'state/ProjectsList/filters/filtersSlice';
import { selectIsUserLd } from 'state/User/userSlice';
import { QuickFilter } from 'utils/constants/projectsList';
import { PersistentFilterTypeKeys } from 'utils/types/filters';

interface UseQuickFiltersParams {
  filterTypeKey: PersistentFilterTypeKeys;
}

export const useQuickFilters = ({ filterTypeKey }: UseQuickFiltersParams) => {
  const dispatch = useAppThunkDispatch();
  const appliedQuickFilters = useSelector(selectQuickFilters);
  const isUserLd = useSelector(selectIsUserLd);
  const [quickFilters, setQuickFilters] = useState<QuickFilter[]>([]);

  useEffect(() => {
    setQuickFilters(appliedQuickFilters ?? []);
  }, [appliedQuickFilters]);

  const toggleQuickFilter = useCallback(
    async (quickFilter: QuickFilter) => {
      const newQuickFilters = quickFilters.includes(quickFilter)
        ? quickFilters.filter((filter) => filter !== quickFilter)
        : [...quickFilters, quickFilter];
      setQuickFilters(newQuickFilters);
      dispatch(setQuickFiltersAction(newQuickFilters));
      dispatch(upsertFiltersSettings({ filterTypeKey }));
      dispatch(
        fetchProjects({
          includeTotalCount: true,
          isConsolidatedView: true,
        })
      );
    },
    [quickFilters, dispatch, filterTypeKey]
  );

  const quickFilterButtons = useMemo(
    () => [
      ...(isUserLd
        ? [
            {
              children: intl.get('PROJECTS_LIST_PAGE.MY_PROJECTS'),
              onClick: () => toggleQuickFilter(QuickFilter.MY_PROJECTS),
              isActive: quickFilters.includes(QuickFilter.MY_PROJECTS),
            },
          ]
        : []),
      {
        children: intl.get('PROJECTS_LIST_PAGE.ACTIVE_PROJECTS'),
        onClick: () => toggleQuickFilter(QuickFilter.OPEN_PROJECTS),
        isActive: quickFilters.includes(QuickFilter.OPEN_PROJECTS),
      },
    ],
    [toggleQuickFilter, quickFilters, isUserLd]
  );

  return { toggleQuickFilter, quickFilterButtons };
};
